/**
 * Карта улья: MapLibre, соты из GET /map/hexes.
 */

import { useEffect, useRef } from "react";
import maplibregl, { type GeoJSONSource, type Map as MapLibreMap } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { UI_ANIMATION } from "@hive/shared";
import type { MapHexListItem } from "@hive/shared/api/map";

import {
  COLORS,
  FOG_FILL_COLOR,
  FOG_FILL_OPACITY,
  HEX_FILL_COLOR,
  baseStyle,
} from "./style.js";
import { fogGeoJson, splitByFog } from "./fog.js";
import { HEX_RESOLUTION, MIN_HEX_ZOOM, hexAt, hexesToGeoJson } from "./hexes.js";
import type { MapBounds } from "../api/map.js";

const SOURCE_ID = "hive-hexes";
const FOG_SOURCE_ID = "hive-fog";
const FRAME_DEBOUNCE_MS = 300;
const CAPTURE_ANIM_MS = UI_ANIMATION.captureHexMs;

/**
 * Сколько разведанных сот держать в памяти между кадрами.
 * Сервер отдаёт туман только для сот в кадре, а покрывало обязано помнить всё
 * открытое: иначе при возврате на прежнее место туман закрывал бы разведанное.
 */
const FOG_MEMORY_LIMIT = 40_000;

export interface HiveMapProps {
  readonly center: { lat: number; lon: number };
  readonly hearthH3: string | null;
  readonly selectedH3: string | null;
  readonly refreshKey?: number;
  readonly captureHexId?: string | null;
  readonly onCaptureAnimationEnd?: () => void;
  readonly onSelect: (h3: string) => void;
  readonly onZoomChange: (zoom: number) => void;
  readonly onFetchFrame: (
    bounds: MapBounds,
    zoom: number,
    signal: AbortSignal,
  ) => Promise<readonly MapHexListItem[]>;
  readonly onFrameError: (code: string) => void;
}

function boundsFromMap(map: MapLibreMap): MapBounds {
  const b = map.getBounds();
  return {
    west: b.getWest(),
    south: b.getSouth(),
    east: b.getEast(),
    north: b.getNorth(),
  };
}

export function HiveMap({
  center,
  hearthH3,
  selectedH3,
  refreshKey = 0,
  captureHexId = null,
  onCaptureAnimationEnd,
  onSelect,
  onZoomChange,
  onFetchFrame,
  onFrameError,
}: HiveMapProps): React.JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const onSelectRef = useRef(onSelect);
  const onZoomChangeRef = useRef(onZoomChange);
  const onFetchFrameRef = useRef(onFetchFrame);
  const onFrameErrorRef = useRef(onFrameError);
  const onCaptureAnimationEndRef = useRef(onCaptureAnimationEnd);
  onSelectRef.current = onSelect;
  onZoomChangeRef.current = onZoomChange;
  onFetchFrameRef.current = onFetchFrame;
  onFrameErrorRef.current = onFrameError;
  onCaptureAnimationEndRef.current = onCaptureAnimationEnd;

  const hearthRef = useRef<string | null>(hearthH3);
  const refreshRef = useRef<(() => void) | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const liftedRef = useRef<Set<string>>(new Set());
  const silhouetteRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: baseStyle(),
      center: [center.lon, center.lat],
      zoom: 16,
      minZoom: 10,
      maxZoom: 19,
      attributionControl: { compact: true },
      trackResize: true,
    });

    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "bottom-right");
    // trackUserLocation: false — иначе geolocate срабатывает на каждое обновление GPS
    // и карточка соты (в т.ч. «Здесь мой дом») всплывает снова и снова.
    // Следы на телефоне пишет Capacitor-трекер, не эта кнопка.
    const geolocate = new maplibregl.GeolocateControl({
      positionOptions: { enableHighAccuracy: true },
      trackUserLocation: false,
      showUserLocation: true,
    });
    geolocate.on("geolocate", (position: GeolocationPosition) => {
      if (map.getZoom() < MIN_HEX_ZOOM) {
        map.setZoom(MIN_HEX_ZOOM);
      }
      onSelectRef.current(hexAt(position.coords.latitude, position.coords.longitude));
    });
    map.addControl(geolocate, "bottom-right");
    // Линейка масштаба в левом нижнем углу: сверху слева стоит колонка панелей
    // интерфейса, и линейка там оказывалась под ней.
    map.addControl(new maplibregl.ScaleControl({ unit: "metric" }), "bottom-left");

    map.on("load", () => {
      map.addSource(SOURCE_ID, {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });

      map.addLayer({
        id: "hex-fill",
        type: "fill",
        source: SOURCE_ID,
        minzoom: MIN_HEX_ZOOM,
        paint: {
          "fill-color": HEX_FILL_COLOR as unknown as string,
          "fill-opacity": [
            "*",
            [
              "case",
              ["==", ["get", "fog"], "dark"],
              0.08,
              ["==", ["get", "fog"], "silhouette"],
              0.45,
              1,
            ],
            [
              "interpolate",
              ["linear"],
              ["get", "vaultFill"],
              0,
              0.18,
              1,
              0.55,
            ],
          ],
        },
      });

      map.addLayer({
        id: "hex-capture",
        type: "fill",
        source: SOURCE_ID,
        minzoom: MIN_HEX_ZOOM,
        filter: ["==", ["get", "h3"], ""],
        paint: {
          "fill-color": COLORS.you,
          "fill-opacity": [
            "*",
            ["coalesce", ["feature-state", "capturePulse"], 0],
            0.7,
          ],
        },
      });

      map.addLayer({
        id: "hex-outline",
        type: "line",
        source: SOURCE_ID,
        minzoom: MIN_HEX_ZOOM,
        paint: {
          "line-color": COLORS.hexOutline,
          "line-width": [
            "case",
            ["==", ["get", "fog"], "dark"],
            0.4,
            1,
          ],
          "line-opacity": [
            "case",
            ["==", ["get", "fog"], "dark"],
            0.25,
            0.7,
          ],
        },
      });

      // Туман лежит поверх сот и подписей карты, но под своим Очагом и выбранной
      // сотой: игрок обязан видеть, что он выделил, даже в неразведанном районе.
      map.addSource(FOG_SOURCE_ID, {
        type: "geojson",
        data: fogGeoJson([], []),
      });

      map.addLayer({
        id: "fog-blanket",
        type: "fill",
        source: FOG_SOURCE_ID,
        paint: {
          "fill-color": FOG_FILL_COLOR as unknown as string,
          "fill-opacity": FOG_FILL_OPACITY as unknown as number,
        },
      });

      // Кромка: размытая линия по краю прорези, чтобы разведанное не обрывалось
      // геометрически ровным шестиугольником, а растворялось в тумане.
      map.addLayer({
        id: "fog-edge",
        type: "line",
        source: FOG_SOURCE_ID,
        paint: {
          "line-color": COLORS.fogEdge,
          "line-width": ["interpolate", ["linear"], ["zoom"], 12, 6, 19, 26],
          "line-blur": ["interpolate", ["linear"], ["zoom"], 12, 4, 19, 18],
          "line-opacity": 0.55,
        },
      });

      map.addLayer({
        id: "hex-hearth-ring",
        type: "line",
        source: SOURCE_ID,
        minzoom: MIN_HEX_ZOOM,
        filter: ["==", ["get", "isHearthRing"], true],
        paint: {
          "line-color": COLORS.ringOutline,
          "line-width": 2,
          "line-dasharray": [2, 1],
        },
      });

      map.addLayer({
        id: "hex-hearth",
        type: "line",
        source: SOURCE_ID,
        minzoom: MIN_HEX_ZOOM,
        filter: ["==", ["get", "isHearth"], true],
        paint: {
          "line-color": COLORS.hearthOutline,
          "line-width": 3,
        },
      });

      map.addLayer({
        id: "hex-selected",
        type: "line",
        source: SOURCE_ID,
        minzoom: MIN_HEX_ZOOM,
        filter: ["==", ["get", "h3"], ""],
        paint: {
          "line-color": "#ffffff",
          "line-width": 2.5,
        },
      });

      /**
       * Запомнить разведку кадра и перерисовать покрывало.
       * Память нужна, потому что кадр — это только текущий экран, а туман
       * рисуется на весь мир.
       */
      const applyFog = (hexes: readonly MapHexListItem[]) => {
        const fogSource = map.getSource<GeoJSONSource>(FOG_SOURCE_ID);
        if (!fogSource) return;

        const { lifted, silhouette } = splitByFog(hexes);
        const silhouetteSet = new Set(silhouette);

        for (const hexId of lifted) {
          liftedRef.current.add(hexId);
          if (silhouetteSet.has(hexId)) {
            silhouetteRef.current.add(hexId);
          } else {
            // Силуэт мог стать разведанной сотой: прорезь остаётся, полупрозрачная плёнка уходит.
            silhouetteRef.current.delete(hexId);
          }
        }

        if (liftedRef.current.size > FOG_MEMORY_LIMIT) {
          liftedRef.current = new Set(lifted);
          silhouetteRef.current = new Set(silhouette);
        }

        fogSource.setData(fogGeoJson([...liftedRef.current], [...silhouetteRef.current]));
      };

      const refresh = () => {
        const source = map.getSource<GeoJSONSource>(SOURCE_ID);
        if (!source) return;
        onZoomChangeRef.current(map.getZoom());
        if (map.getZoom() < MIN_HEX_ZOOM) {
          // Сетку сот не рисуем — мелко и тяжело, но туман остаётся на месте.
          source.setData({ type: "FeatureCollection", features: [] });
          return;
        }

        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
          abortRef.current?.abort();
          const controller = new AbortController();
          abortRef.current = controller;
          const bounds = boundsFromMap(map);
          const zoom = map.getZoom();

          void onFetchFrameRef
            .current(bounds, zoom, controller.signal)
            .then((hexes) => {
              if (controller.signal.aborted) return;
              source.setData(hexesToGeoJson(hexes, hearthRef.current));
              applyFog(hexes);
            })
            .catch((err: unknown) => {
              if (controller.signal.aborted) return;
              const code =
                err && typeof err === "object" && "code" in err
                  ? String((err as { code: string }).code)
                  : "network.offline";
              onFrameErrorRef.current(code);
            });
        }, FRAME_DEBOUNCE_MS);
      };

      refreshRef.current = refresh;
      refresh();

      map.on("moveend", refresh);
      map.on("zoomend", refresh);
    });

    map.on("click", (event) => {
      if (map.getZoom() < MIN_HEX_ZOOM) return;
      onSelectRef.current(hexAt(event.lngLat.lat, event.lngLat.lng));
    });

    map.getCanvas().style.cursor = "crosshair";
    mapRef.current = map;

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      abortRef.current?.abort();
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    hearthRef.current = hearthH3;
    refreshRef.current?.();
  }, [hearthH3]);

  useEffect(() => {
    refreshRef.current?.();
  }, [refreshKey]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map?.getLayer("hex-selected")) return;
    map.setFilter("hex-selected", ["==", ["get", "h3"], selectedH3 ?? ""]);
  }, [selectedH3]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map?.getLayer("hex-capture") || !captureHexId) return;

    map.setFilter("hex-capture", ["==", ["get", "h3"], captureHexId]);
    const started = performance.now();
    let frameId = 0;

    const tick = (now: number) => {
      const pulse = Math.min(1, (now - started) / CAPTURE_ANIM_MS);
      map.setFeatureState({ source: SOURCE_ID, id: captureHexId }, { capturePulse: pulse });
      if (pulse < 1) {
        frameId = requestAnimationFrame(tick);
        return;
      }
      map.setFeatureState({ source: SOURCE_ID, id: captureHexId }, { capturePulse: 0 });
      onCaptureAnimationEndRef.current?.();
    };

    frameId = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frameId);
      try {
        map.removeFeatureState({ source: SOURCE_ID, id: captureHexId }, "capturePulse");
      } catch {
        /* карта могла уже размонтироваться */
      }
    };
  }, [captureHexId]);

  return <div ref={containerRef} className="map" data-resolution={HEX_RESOLUTION} />;
}
